import React, { useState } from 'react';
import { Modal, Button, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faLock, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  FormSection,
  DangerSection,
  DangerSectionTitle,
  DangerSectionDescription,
  DangerBox,
  DangerBoxIcon,
  DangerBoxContent,
  DangerBoxTitle,
  DangerBoxDescription,
  DangerButton,
  DeactivateButton,
  ConfirmationInput,
  WarningBox,
  WarningTextSmall,
} from './Styles';

interface AccountDangerSectionProps {
  loading?: boolean;
  onDeactivate?: () => void;
  onDelete?: () => void;
}

const AccountDangerSection: React.FC<AccountDangerSectionProps> = ({
  loading = false,
  onDeactivate,
  onDelete,
}) => {
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deactivateConfirmation, setDeactivateConfirmation] = useState('');

  const handleDeactivateClick = () => {
    setDeactivateConfirmation('');
    setDeactivateModal(true);
  };

  const handleDeactivateConfirm = () => {
    if (deactivateConfirmation.toLowerCase() === 'deactivate') {
      onDeactivate?.();
      setDeactivateModal(false);
      setDeactivateConfirmation('');
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmation('');
    setDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmation.toLowerCase() === 'delete') {
      onDelete?.();
      setDeleteModal(false);
      setDeleteConfirmation('');
    }
  };

  return (
    <FormSection>
      <DangerSection>
        <DangerSectionTitle>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Danger Zone
        </DangerSectionTitle>
        <DangerSectionDescription>
          These actions cannot be undone. Please proceed with extreme caution.
        </DangerSectionDescription>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <DangerBox $isDanger={false}>
            <DangerBoxIcon>
              <FontAwesomeIcon icon={faLock} />
            </DangerBoxIcon>
            <DangerBoxContent>
              <DangerBoxTitle>Deactivate Account</DangerBoxTitle>
              <DangerBoxDescription>
                Temporarily disable your account. Your data will be preserved and you can reactivate anytime by logging in.
              </DangerBoxDescription>
            </DangerBoxContent>
            <DeactivateButton
              onClick={handleDeactivateClick}
              loading={loading}
            >
              Deactivate
            </DeactivateButton>
          </DangerBox>

          <DangerBox $isDanger={true}>
            <DangerBoxIcon>
              <FontAwesomeIcon icon={faTrash} />
            </DangerBoxIcon>
            <DangerBoxContent>
              <DangerBoxTitle>Delete Account</DangerBoxTitle>
              <DangerBoxDescription>
                Permanently remove your account and all associated data. This action is irreversible.
              </DangerBoxDescription>
            </DangerBoxContent>
            <DangerButton
              onClick={handleDeleteClick}
              loading={loading}
            >
              Delete
            </DangerButton>
          </DangerBox>
        </div>
      </DangerSection>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FontAwesomeIcon icon={faLock} style={{ color: '#ff9800', fontSize: '18px' }} />
            <span>Deactivate Your Account</span>
          </div>
        }
        open={deactivateModal}
        onCancel={() => {
          setDeactivateModal(false);
          setDeactivateConfirmation('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setDeactivateModal(false);
            setDeactivateConfirmation('');
          }}>
            Cancel
          </Button>,
          <DeactivateButton
            key="confirm"
            onClick={handleDeactivateConfirm}
            disabled={deactivateConfirmation.toLowerCase() !== 'deactivate'}
            loading={loading}
          >
            Yes, Deactivate
          </DeactivateButton>,
        ]}
        width={500}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography.Text>
            Your account will be temporarily suspended. You can reactivate it anytime by logging back in with your credentials.
          </Typography.Text>

          <WarningBox $type="warning">
            <WarningTextSmall>
              <strong>What happens:</strong> Dashboard access will be blocked, but all your data will be preserved.
            </WarningTextSmall>
          </WarningBox>

          <div>
            <Typography.Text style={{ fontSize: '14px', fontWeight: 500 }}>
              To confirm, type <strong style={{ color: '#ff9800' }}>deactivate</strong>
            </Typography.Text>
            <ConfirmationInput
              placeholder='Type "deactivate" to confirm'
              value={deactivateConfirmation}
              onChange={(e) => setDeactivateConfirmation(e.target.value)}
              status={
                deactivateConfirmation && deactivateConfirmation.toLowerCase() !== 'deactivate'
                  ? 'error'
                  : undefined
              }
            />
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FontAwesomeIcon icon={faTrash} style={{ color: '#f5222d', fontSize: '18px' }} />
            <span>Delete Account Permanently</span>
          </div>
        }
        open={deleteModal}
        onCancel={() => {
          setDeleteModal(false);
          setDeleteConfirmation('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setDeleteModal(false);
            setDeleteConfirmation('');
          }}>
            Cancel
          </Button>,
          <DangerButton
            key="confirm"
            onClick={handleDeleteConfirm}
            disabled={deleteConfirmation.toLowerCase() !== 'delete'}
            loading={loading}
          >
            Yes, Delete Everything
          </DangerButton>,
        ]}
        width={500}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography.Text style={{ fontSize: '14px', fontWeight: 500 }}>
            This will permanently delete your account and all data:
          </Typography.Text>

          <ul style={{ marginLeft: '20px', fontSize: '13px', color: '#666', lineHeight: '1.8' }}>
            <li>Profile information and settings</li>
            <li>All inventory, items, and customizations</li>
            <li>Transaction and order history</li>
            <li>Uploaded files and media</li>
            <li>Account preferences and API keys</li>
          </ul>

          <WarningBox $type="danger">
            <WarningTextSmall>
              <strong>⚠️ Warning:</strong> This action cannot be undone. All your data will be permanently deleted.
            </WarningTextSmall>
          </WarningBox>

          <div>
            <Typography.Text style={{ fontSize: '14px', fontWeight: 500 }}>
              To confirm, type <strong style={{ color: '#f5222d' }}>delete</strong>
            </Typography.Text>
            <ConfirmationInput
              placeholder='Type "delete" to confirm'
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              status={
                deleteConfirmation && deleteConfirmation.toLowerCase() !== 'delete'
                  ? 'error'
                  : undefined
              }
            />
          </div>
        </div>
      </Modal>
    </FormSection>
  );
};

export default AccountDangerSection;
